import { useCallback, useRef, useState } from 'react';
import type { UserMenuUsageSummary } from '@pubmed-seq-reader/shared/schemas';
import { Languages, Shield, LogOut, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { authClient } from '@/lib/auth';
import { buildSettingsRoute } from '@/lib/hash-route';
import { normalizeLanguage, type SupportedLanguage } from '@/i18n/language';
import { accountApi } from '@/services/api';

const MENU_USAGE_REFRESH_INTERVAL_MS = 60 * 1000;

function formatBytes(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const digits = value >= 100 ? 0 : value >= 10 ? 1 : 2;
  return `${value.toFixed(digits)} ${units[unitIndex]}`;
}

function formatTokensInMillions(tokens: number) {
  const normalized = Number(tokens);
  if (!Number.isFinite(normalized) || normalized <= 0) return '0M';
  const valueInMillions = normalized / 1_000_000;
  const digits = valueInMillions >= 100 ? 0 : valueInMillions >= 10 ? 1 : 2;
  return `${valueInMillions.toFixed(digits)}M`;
}

export function UserAccountMenu() {
  const { t, i18n } = useTranslation();
  const sessionState = authClient.useSession();
  const session = (
    sessionState.data || null
  ) as { user?: { email?: string; image?: string | null; name?: string | null; role?: string } } | null;

  if (sessionState.isPending || !session?.user) {
    return null;
  }

  const email = session.user.email || t('account.signedIn');
  const isAdmin = session.user.role === 'admin';
  const currentLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const avatarFallback = (session.user.name?.trim()?.charAt(0) || email.charAt(0) || 'U').toUpperCase();
  const [usageSummary, setUsageSummary] = useState<UserMenuUsageSummary | null>(null);
  const [loadingUsageSummary, setLoadingUsageSummary] = useState(false);
  const [usageSummaryError, setUsageSummaryError] = useState(false);
  const lastUsageSummaryLoadedAtRef = useRef(0);
  const usageSummaryRequestIdRef = useRef(0);

  const handleLanguageChange = (language: SupportedLanguage) => {
    if (language === currentLanguage) return;
    void i18n.changeLanguage(language);
  };

  const loadUsageSummary = useCallback(async () => {
    const now = Date.now();
    if (loadingUsageSummary) return;
    if (usageSummary && now - lastUsageSummaryLoadedAtRef.current < MENU_USAGE_REFRESH_INTERVAL_MS) {
      return;
    }

    const requestId = usageSummaryRequestIdRef.current + 1;
    usageSummaryRequestIdRef.current = requestId;
    setLoadingUsageSummary(true);
    setUsageSummaryError(false);
    try {
      const summary = await accountApi.getUserMenuUsageSummary();
      if (usageSummaryRequestIdRef.current !== requestId) return;
      setUsageSummary(summary);
      lastUsageSummaryLoadedAtRef.current = Date.now();
    } catch {
      if (usageSummaryRequestIdRef.current !== requestId) return;
      setUsageSummaryError(true);
    } finally {
      if (usageSummaryRequestIdRef.current === requestId) {
        setLoadingUsageSummary(false);
      }
    }
  }, [loadingUsageSummary, usageSummary]);

  const usageUnavailableText = t('account.menu.usage.unavailable');
  const usageLoadingText = t('account.menu.usage.loading');
  const usageDatabaseValue =
    loadingUsageSummary && !usageSummary
      ? usageLoadingText
      : usageSummaryError
        ? usageUnavailableText
        : usageSummary
          ? formatBytes(usageSummary.totalBytes)
          : '-';
  const usageTokensValue =
    loadingUsageSummary && !usageSummary
      ? usageLoadingText
      : usageSummaryError
        ? usageUnavailableText
        : usageSummary
          ? formatTokensInMillions(usageSummary.totalTokens)
          : '-';

  return (
    <DropdownMenu
      onOpenChange={open => {
        if (!open) return;
        void loadUsageSummary();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          aria-label={t('account.menu.open')}
        >
          <Avatar className="h-10 w-10 border border-slate-200 bg-white shadow-sm">
            <AvatarImage src={session.user.image || undefined} alt={email} />
            <AvatarFallback>{avatarFallback}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="space-y-1">
          <p className="truncate text-sm font-medium text-slate-800">{email}</p>
          <Badge variant={isAdmin ? 'default' : 'secondary'} className="w-fit text-[11px]">
            {isAdmin ? t('account.role.admin') : t('account.role.user')}
          </Badge>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
          <Languages className="h-3.5 w-3.5" />
          {t('language.label')}
        </DropdownMenuLabel>
        <DropdownMenuItem className="cursor-pointer" onSelect={() => handleLanguageChange('en')}>
          {t('language.english')}
          {currentLanguage === 'en' ? (
            <span className="ml-auto text-[11px] text-slate-500">{t('language.current')}</span>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem className="cursor-pointer" onSelect={() => handleLanguageChange('zh')}>
          {t('language.chinese')}
          {currentLanguage === 'zh' ? (
            <span className="ml-auto text-[11px] text-slate-500">{t('language.current')}</span>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="space-y-1.5">
          <div className="text-xs uppercase tracking-wide text-slate-500">{t('account.menu.usage.title')}</div>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-600">
            <span>{t('account.menu.usage.database')}</span>
            <span className="text-slate-800">{usageDatabaseValue}</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-600">
            <span>{t('account.menu.usage.tokensAllTime')}</span>
            <span className="text-slate-800">{usageTokensValue}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer"
          onSelect={() => {
            window.location.hash = buildSettingsRoute();
          }}
        >
          <Settings className="mr-2 h-4 w-4" />
          {t('account.menu.settings')}
        </DropdownMenuItem>
        {isAdmin ? (
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => {
              window.location.hash = '/admin/dashboard';
            }}
          >
            <Shield className="mr-2 h-4 w-4" />
            {t('account.menu.adminDashboard')}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer text-red-600 focus:text-red-600"
          onSelect={async () => {
            await authClient.signOut();
            window.location.reload();
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          {t('account.menu.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
